import { API_BASE_URL } from "../constants";
import { apiFetch, apiRequest } from "./api.client";
import type { DepartmentOverviewRow } from "./dashboard.service";

export type Period = "daily" | "weekly" | "monthly";
export type ReportType = "revenue" | "patients" | "appointments" | "diagnoses" | "departments";

export interface PatientTrendBucket {
  bucket: string;
  new_patients: number;
  returning_patients: number;
}

export interface AppointmentUtilisation {
  booked_slots: number;
  available_slots: number;
  utilisation_pct: number;
  cancellation_rate_pct: number;
  no_show_rate_pct: number;
}

export interface RevenueBucket {
  bucket: string;
  amount_paise: number;
  invoice_count: number;
}

export interface DiagnosisRow {
  rank: number;
  diagnosis: string;
  icd10_code: string | null;
  count: number;
}

// ── Accounting: P&L / cash-flow / breakdown ─────────────────────────────────

export type PnlPeriod = "month" | "quarter" | "year";

export interface PnlBucket {
  bucket: string;
  income_paise: number;
  expense_paise: number;
  net_paise: number;
  is_expense_anomaly: boolean;
}

export interface CashFlowBucket {
  bucket: string;
  money_in_paise: number;
  money_out_paise: number;
  net_paise: number;
  is_outflow_anomaly: boolean;
}

export interface BreakdownRow {
  kind: "expense_category" | "income_source" | "invoice_revenue";
  label: string;
  amount_paise: number;
}

export interface ConsolidatedPnlRow {
  clinic_id: string;
  clinic_name: string;
  bucket: string;
  income_paise: number;
  expense_paise: number;
  net_paise: number;
}

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v !== undefined && search.set(k, v));
  return `?${search.toString()}`;
}

/** Shared download helper for the Accounting module's dedicated CSV/PDF
 * export endpoints (distinct from the legacy /reports/export dispatch used
 * by downloadCsv below — see reports.py's module docstring on why these are
 * separate: no feature-flag gate, Admin-only, different filename convention). */
async function downloadFile(path: string, filename: string): Promise<void> {
  const url = `${API_BASE_URL}${path}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      message = body?.error?.message || body?.detail?.reason || body?.detail || message;
    } catch { /* keep status fallback */ }
    throw new Error(message);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export const ReportsAPI = {
  patients: (from: string, to: string, period: Period = "daily") =>
    apiRequest<PatientTrendBucket[]>(`/reports/patients${qs({ from, to, period })}`),

  appointments: (from: string, to: string) =>
    apiRequest<AppointmentUtilisation>(`/reports/appointments${qs({ from, to })}`),

  departments: (from: string, to: string) =>
    apiRequest<DepartmentOverviewRow[]>(`/reports/departments${qs({ from, to })}`),

  diagnoses: (from: string, to: string) =>
    apiRequest<DiagnosisRow[]>(`/reports/diagnoses${qs({ from, to })}`),

  // Revenue: HTTP 501 until S8-01 lands. Caller should handle ApiError(501).
  revenue: (from: string, to: string, period: Period = "daily") =>
    apiRequest<RevenueBucket[]>(`/reports/revenue${qs({ from, to, period })}`),

  /**
   * CSV export — bypasses apiRequest's JSON envelope unwrap.
   * Triggers a browser download via a temporary anchor + object URL.
   */
  async downloadCsv(report: ReportType, from: string, to: string): Promise<void> {
    const url = `${API_BASE_URL}/reports/export${qs({ report, from, to })}`;
    const response = await apiFetch(url);
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        message = body?.error?.message || body?.detail?.reason || body?.detail || message;
      } catch { /* keep status fallback */ }
      throw new Error(message);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `kaagazy_report_${report}_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  },

  // ── Accounting: P&L / cash-flow / breakdown ───────────────────────────────

  pnl: (from: string, to: string, period: PnlPeriod = "month") =>
    apiRequest<PnlBucket[]>(`/reports/pnl${qs({ from, to, period })}`),

  cashFlow: (from: string, to: string, period: Period = "daily") =>
    apiRequest<CashFlowBucket[]>(`/reports/cash-flow${qs({ from, to, period })}`),

  breakdown: (from: string, to: string) =>
    apiRequest<BreakdownRow[]>(`/reports/breakdown${qs({ from, to })}`),

  consolidatedPnl: (clinicGroupId: string, from: string, to: string, period: PnlPeriod = "month") =>
    apiRequest<ConsolidatedPnlRow[]>(
      `/reports/consolidated/pnl${qs({ clinic_group_id: clinicGroupId, from, to, period })}`
    ),

  downloadPnlCsv: (from: string, to: string, period: PnlPeriod = "month") =>
    downloadFile(`/reports/pnl/export${qs({ from, to, period })}`, `kaagazy_pnl_${from}_${to}.csv`),

  downloadPnlPdf: (from: string, to: string, period: PnlPeriod = "month") =>
    downloadFile(`/reports/pnl/export-pdf${qs({ from, to, period })}`, `kaagazy_pnl_${from}_${to}.pdf`),

  downloadCashFlowCsv: (from: string, to: string, period: Period = "daily") =>
    downloadFile(`/reports/cash-flow/export${qs({ from, to, period })}`, `kaagazy_cash_flow_${from}_${to}.csv`),

  downloadCashFlowPdf: (from: string, to: string, period: Period = "daily") =>
    downloadFile(`/reports/cash-flow/export-pdf${qs({ from, to, period })}`, `kaagazy_cash_flow_${from}_${to}.pdf`),

  downloadBreakdownCsv: (from: string, to: string) =>
    downloadFile(`/reports/breakdown/export${qs({ from, to })}`, `kaagazy_breakdown_${from}_${to}.csv`),

  downloadBreakdownPdf: (from: string, to: string) =>
    downloadFile(`/reports/breakdown/export-pdf${qs({ from, to })}`, `kaagazy_breakdown_${from}_${to}.pdf`),
};
